import { useState } from "react";
import { Alert, Card, IconButton, Snackbar } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { loadMembersPageFromBackend, deleteMember } from "app/services/member.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { memberPageActions } from "store/member-page-slice";
import { useServerPaginatedGrid } from "app/hooks/useServerPaginatedGrid";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import AddMemberDialog from "./AddMemberDialog";
import { ConfirmDialog } from "../ConfirmDialog";

// Frontend grid field -> backend sortBy column (allowlisted server-side).
const SORT_FIELD_MAP = {
  id: "id",
  name: "name",
  email: "clientEmail",
};

const AllMembersView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");

  const {
    rows,
    totalElements,
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchQuery,
    loading,
    fetchPage,
    handlePaginationModelChange,
    handleSortModelChange,
    handleSearchChange,
  } = useServerPaginatedGrid({
    selector: (state) => state.memberPage,
    actions: memberPageActions,
    fetchFn: loadMembersPageFromBackend,
  });

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this Client Team Member (Name: '${data.name}')?`);
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteMember(deleteId)
      .then(() => fetchPage())
      .catch((error) => {
        const parsed = parseApiError(error);
        if (parsed.isUnauthorized) {
          handleAxiosError(error);
          return;
        }
        setSnackMessage(parsed.message || "Failed to delete member. Please try again.");
        setSnackOpen(true);
      });
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Member Id", width: 120 },
    { field: "name", headerName: "Member Name", width: 250 },
    { field: "client_code", headerName: "Client / Account", width: 150, sortable: false, valueGetter: (value, row) => row.client?.code ?? "—" },
    { field: "email", headerName: "Email", width: 250 },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            InOpTra's Team Members - Client Mappings
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <PaginatedDataGrid
            rows={rows}
            columns={columns}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            sortFieldMap={SORT_FIELD_MAP}
            defaultSortBy="name"
            defaultSortDirection="ASC"
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAdd={() => {
              setEditObject(null);
              setOpen(true);
            }}
            addLabel="Add InOpTra's Team Member"
            columnVisibilityModel={{ id: false }}
          />
        </MDBox>
      </Card>
      <AddMemberDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={() => fetchPage()}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Member?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="error" sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AllMembersView;
